/**
 * คู่ฝึก (sparring partners, docs/GDD.md): a row of challengers beside the duelling ground (data/duelGround.ts), one
 * per realm of the power ranks (cultivation.ts `REALMS`, `rankFor`), from a beginner to a master. Each is a person
 * of a school's style fighting with an existing art and neigong (the arena roster's build shape, data/arena.ts).
 * Challenged by talking to them (ท้าประลอง), they always take it up, and the bout is the ring's open challenge
 * (systems/duelGround.ts): the countdown, in the ring, to a tenth of the health, nothing lost.
 *
 * A tier is locked until the one before it is beaten. The first win over each tier pays `first` once (the top tier
 * adds a title); later wins pay `repeat`, at most `SPARRING.dailyWins` paid wins a day.
 *
 * Data only; the rules are `sparring.ts`.
 */
import type { ArenaBuild } from './arena';
import type { PassReward } from './pass';
import { PLAINS_SPAR_SPOTS } from './plainsFill';
import { plains256 } from './zones';

export interface SparTier {
  readonly id: string;
  /** Thai: the tier's name over the challenger. */
  readonly name: string;
  /** The recommended power rank (cultivation.ts `rankFor` index): the first stage of its realm. */
  readonly rank: number;
  /** The school style it fights in (Thai, for the title line). */
  readonly style: string;
  /** Who stands there and what they fight with. */
  readonly person: ArenaBuild;
  /** Paid for the first win over this tier. */
  readonly first: PassReward;
  /** Paid for each later win (within the daily cap). */
  readonly repeat: { readonly gold: number; readonly cultivation: number };
}

export const SPARRING = {
  /** Paid repeat wins a day (all tiers together). */
  dailyWins: 5,
} as const;

const tier = (
  k: number,
  id: string,
  name: string,
  style: string,
  person: Omit<ArenaBuild, 'title'>,
  gold: number,
  cultivation: number,
  title?: string,
): SparTier => ({
  id,
  name,
  rank: k * 9,
  style,
  person: { ...person, title: name },
  first: { gold, cultivation, ...(title ? { title } : {}) },
  repeat: { gold: Math.round(gold / 10), cultivation: Math.round(cultivation / 10) },
});

export const SPAR_TIERS: readonly SparTier[] = [
  tier(0, 'spar-body', 'คู่ฝึกขั้นฝึกกาย', 'หมัดหินระฆัง', {
    name: 'ศิษย์ฝึกหมัดอาหนิว',
    sprite: 'monk',
    art: 'bell-fist',
    skillLevel: 1,
    neigong: 'blood-oath',
    neigongLevel: 1,
    equipment: { body: 'navy-coat' },
  }, 100, 50),
  tier(1, 'spar-qi', 'คู่ฝึกขั้นฝึกลมปราณ', 'หมัดหมอกวน', {
    name: 'นักพรตน้อยชิงเฟิง',
    sprite: 'taoist',
    art: 'drift-fist',
    skillLevel: 2,
    neigong: 'cloud-heart',
    neigongLevel: 4,
    equipment: { body: 'navy-coat', head: 'red-band' },
  }, 300, 120),
  tier(2, 'spar-third', 'คู่ฝึกชั้นสาม', 'กระบี่สายลม', {
    name: 'องครักษ์หนุ่มเฉินอวี่',
    sprite: 'guardOfficer',
    art: 'breeze-sword',
    skillLevel: 4,
    neigong: 'blood-oath',
    neigongLevel: 7,
    equipment: { weapon: 'iron-sword', body: 'black-vest', head: 'red-band' },
  }, 600, 250),
  tier(3, 'spar-second', 'คู่ฝึกชั้นสอง', 'ฝ่ามือเมฆา', {
    name: 'แม่นางม่วงหลิงเยว่',
    sprite: 'lilac',
    art: 'cloud-palm',
    skillLevel: 5,
    neigong: 'cloud-heart',
    neigongLevel: 10,
    equipment: { body: 'wolf-pelt', head: 'bamboo-hat' },
  }, 1000, 450),
  tier(4, 'spar-first', 'คู่ฝึกชั้นหนึ่ง', 'กระบี่ธารเยือกแข็ง', {
    name: 'กระบี่ราตรีเย่หาน',
    sprite: 'night',
    art: 'snow-sword',
    skillLevel: 7,
    neigong: 'blood-oath',
    neigongLevel: 13,
    equipment: { weapon: 'azure-sword', body: 'black-vest', head: 'bamboo-hat' },
  }, 1600, 700),
  tier(5, 'spar-peerless', 'คู่ฝึกไร้เทียมทาน', 'กระบี่พู่กัน', {
    name: 'บัณฑิตพู่กันเหล็กซูเหวิน',
    sprite: 'scholar',
    art: 'ink-slate',
    skillLevel: 8,
    neigong: 'cloud-heart',
    neigongLevel: 16,
    equipment: { weapon: 'azure-sword', body: 'crimson-coat', head: 'jade-crown' },
  }, 2400, 1000),
  tier(6, 'spar-master', 'คู่ฝึกปรมาจารย์', 'กระบี่วงธารา', {
    name: 'ปรมาจารย์อู่หยาง',
    sprite: 'heroWudang',
    art: 'drift-sword',
    skillLevel: 10,
    neigong: 'blood-oath',
    neigongLevel: 20,
    equipment: { weapon: 'crimson-sword', body: 'crimson-coat', head: 'jade-crown' },
  }, 4000, 1500, 'ring-master'),
];

/** Where each tier's challenger stands, by zone: a row west of the ring, along the road to its gate. */
export const SPAR_SPOTS: Readonly<Record<string, readonly (readonly [number, number])[]>> = {
  yanyu2: [
    [466, 283],
    [466, 287],
    [466, 291],
    [466, 295],
    [466, 305],
    [466, 309],
    [466, 313],
  ],
};

/** The คู่ฝึก row of a zone (the 256 m plains have one only with the switch on: data/plainsFill.ts). */
export const sparSpotsIn = (zone: string): readonly (readonly [number, number])[] =>
  zone === 'plains' ? (plains256() ? PLAINS_SPAR_SPOTS : []) : (SPAR_SPOTS[zone] ?? []);
